import type { EmailStyleSettings } from "../../style-settings";
import { EMAIL_MIXINS, EMAIL_TOKENS } from "../../styles/tokens";
import { applyStyleMap } from "../shared-block-styles";

import { CSSProperties } from "react";

type ImageAlignment = "left" | "center" | "right";

const ALIGNMENT_MARGIN: Record<ImageAlignment, string> = {
  left: "0",
  center: "0 auto",
  right: "0 0 0 auto",
};

/**
 * An image's appearance, for the canvas and for the inbox alike.
 *
 * The block's own `width` control offers four percentages; anything else a
 * designer wrote — a pixel width, a third of the row — is in the style store
 * and lands last, so the design's own sizing is what shows.
 */
export function getImageStyles(
  attrs: Record<string, unknown>,
  settings?: Partial<EmailStyleSettings>,
): CSSProperties {
  const width = attrs.width as "auto" | "25%" | "50%" | "75%" | "100%";
  const maxHeight = attrs.maxHeight as string;

  const styles: CSSProperties = {
    ...EMAIL_MIXINS.responsiveImage,
    width: width === "auto" ? "auto" : width,
    margin: ALIGNMENT_MARGIN[attrs.alignment as ImageAlignment] ?? "0",
    borderRadius: EMAIL_TOKENS.borderRadius.sm,
  };

  // A capped height is the one case where the width has to give way, or the
  // image is squashed rather than scaled.
  if (maxHeight) {
    styles.height = maxHeight;
    styles.width = "auto";
    styles.objectFit = "contain";
  }

  if (settings?.imageBorderRadius != null) {
    styles.borderRadius = `${settings.imageBorderRadius}px`;
  }

  applyStyleMap(styles, attrs);
  return styles;
}

/** Placeholder styles for the editor view (browser, not email output). Uses CSS variables for dark mode. */
export function getImagePlaceholderStyles(): CSSProperties {
  return {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "120px",
    border: "2px dashed rgb(var(--border))",
    borderRadius: EMAIL_TOKENS.borderRadius.md,
    backgroundColor: "rgb(var(--background-secondary))",
    color: "rgb(var(--foreground-muted))",
    cursor: "pointer",
  };
}
